/**
 * @file accessibilityState.ts
 * @description Bridges React Native's legacy `accessibilityState` object to the
 * modern `aria-*` props that react-native-web actually maps (SE-6297).
 *
 * ## Why this exists
 *
 * react-native-web 0.21.2 maps ~50 individual accessibility props — including
 * `aria-selected` and the deprecated `accessibilitySelected` — but it has NO
 * handler for the `accessibilityState` OBJECT. It is dropped on the floor. The
 * only reference to anything similar in the whole package is `accessibilityStates`
 * (plural, a different and older prop) inside `AccessibilityUtil/isDisabled.js`.
 *
 * The consequence is silent: the control renders and looks correct, but assistive
 * technology is never told its state. Measured on dev in SE-6265 — all three Add
 * Players source tabs reached the DOM with no `aria-selected` at all, while the
 * app's own bottom tab bar (expo-router's `BottomTabItem`, which uses the modern
 * props) emitted it correctly in the same snapshot.
 *
 * ## Why the derivation is ADDITIVE
 *
 * `accessibilityState` is the prop NATIVE honors. Call sites must therefore keep
 * passing it, and atoms must keep forwarding it — swapping call sites over to
 * `aria-*` alone would fix web by regressing native. This helper adds the web
 * spelling alongside the native one so both surfaces are correct.
 *
 * ## Why booleans are emitted explicitly
 *
 * An unselected control must announce `aria-selected="false"` rather than
 * omitting the attribute. ARIA treats a missing attribute as "state not
 * applicable", so omitting it leaves the unselected tabs just as unannounced as
 * the bug this replaces. Only `undefined` keys are skipped.
 * @module components/atoms/accessibilityState
 */

import type { AccessibilityState } from "react-native";

/** The `aria-*` props derived from an {@link AccessibilityState}. */
export interface DerivedAriaProps {
  /** Mirrors `accessibilityState.selected`. */
  readonly "aria-selected"?: boolean;
  /** Mirrors `accessibilityState.checked`; `"mixed"` is a valid ARIA value. */
  readonly "aria-checked"?: boolean | "mixed";
  /** Mirrors `accessibilityState.expanded`. */
  readonly "aria-expanded"?: boolean;
  /** Mirrors `accessibilityState.disabled`. */
  readonly "aria-disabled"?: boolean;
  /** Mirrors `accessibilityState.busy`. */
  readonly "aria-busy"?: boolean;
}

/**
 * Derive the web-mapped `aria-*` props from a native `accessibilityState`.
 * @remarks
 * Keys whose state value is `undefined` are omitted entirely, so this never
 * invents an attribute the caller did not describe. `false` IS emitted — see the
 * module docs for why that distinction is load-bearing.
 * @param state - The native accessibility state, if the caller passed one.
 * @returns The derived `aria-*` props; empty when there is nothing to describe.
 */
export const deriveAriaFromAccessibilityState = (
  state: AccessibilityState | undefined
): DerivedAriaProps => {
  if (state === undefined) return {};

  return {
    ...(state.selected !== undefined
      ? { "aria-selected": state.selected }
      : {}),
    ...(state.checked !== undefined ? { "aria-checked": state.checked } : {}),
    ...(state.expanded !== undefined
      ? { "aria-expanded": state.expanded }
      : {}),
    ...(state.disabled !== undefined
      ? { "aria-disabled": state.disabled }
      : {}),
    ...(state.busy !== undefined ? { "aria-busy": state.busy } : {}),
  };
};
